// src/pages/OrdersTab.js
import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'
import { ChevronDown, ChevronRight, Download, Search } from 'lucide-react'
import { Spinner, TableWrap, Th, Td, Badge, formatRupees, formatTime, SectionTitle } from '../components/ui'

const ALL_STATUSES = ['PENDING','CONFIRMED','READY','PICKED_UP','CANCELLED','FAILED','REFUND_PENDING','REFUNDED']
const DEFAULT_STATUSES = ['FAILED','PICKED_UP']
const PAGE_SIZE = 50

export default function OrdersTab() {
  const [orders,     setOrders]     = useState([])
  const [loading,    setLoading]    = useState(true)
  const [statuses,   setStatuses]   = useState(DEFAULT_STATUSES)
  const [canteenFilter, setCanteen] = useState(null)
  const [canteens,   setCanteens]   = useState([])
  const [dateFrom,   setDateFrom]   = useState(todayStart())
  const [dateTo,     setDateTo]     = useState(null)
  const [cursor,     setCursor]     = useState(null) // { ts, id }
  const [hasMore,    setHasMore]    = useState(false)
  const [expanded,   setExpanded]   = useState(null)
  const [detail,     setDetail]     = useState({})

  function todayStart() {
    const d = new Date(); d.setHours(0,0,0,0); return d.toISOString()
  }

  // Load canteens for dropdown
  useEffect(() => {
    supabase.from('canteens').select('id, name').then(({ data }) => { if (data) setCanteens(data) })
  }, [])

  const load = useCallback(async (append = false, cur = null) => {
    setLoading(true)
    const { data } = await supabase.rpc('get_all_orders_overview', {
      p_statuses:   statuses.length > 0 ? statuses : null,
      p_canteen_id: canteenFilter,
      p_date_from:  dateFrom || null,
      p_date_to:    dateTo || null,
      p_cursor_ts:  cur?.ts || null,
      p_cursor_id:  cur?.id || null,
      p_page_size:  PAGE_SIZE,
    })
    const rows = data || []
    const more = rows.length > PAGE_SIZE
    const display = more ? rows.slice(0, PAGE_SIZE) : rows
    setHasMore(more)
    setOrders(prev => append ? [...prev, ...display] : display)
    if (display.length > 0) {
      const last = display[display.length - 1]
      setCursor({ ts: last.created_at, id: last.order_id })
    }
    setLoading(false)
  }, [statuses, canteenFilter, dateFrom, dateTo])

  useEffect(() => { setCursor(null); load(false, null) }, [statuses, canteenFilter, dateFrom, dateTo])

  const loadMore = () => load(true, cursor)

  const toggleStatus = (s) => {
    setStatuses(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s])
  }

  const expandRow = async (orderId) => {
    if (expanded === orderId) { setExpanded(null); return }
    setExpanded(orderId)
    if (!detail[orderId]) {
      const { data } = await supabase.rpc('get_order_detail', { p_order_id: orderId })
      setDetail(prev => ({ ...prev, [orderId]: data }))
    }
  }

  const exportCSV = () => {
    const headers = ['order_id','canteen','status','amount','slot','token','created_at','txn_id']
    const rows = orders.map(o => [
      o.order_id, o.canteen_name, o.status, o.total_amount,
      o.pickup_slot, o.token_number, o.created_at, o.txn_id || ''
    ])
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url
    a.download = `orders_${new Date().toISOString().slice(0,10)}.csv`
    a.click(); URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-4">

      {/* Filters */}
      <div className="bg-[#1c1c1e] rounded-2xl p-4 border border-white/5 space-y-3">
        {/* Status multi-select */}
        <div>
          <p className="text-gray-500 text-[10px] uppercase tracking-widest font-bold mb-2">Status filter</p>
          <div className="flex flex-wrap gap-2">
            {ALL_STATUSES.map(s => (
              <button key={s} onClick={() => toggleStatus(s)}
                className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border transition-all ${
                  statuses.includes(s)
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white/5 text-gray-500 border-white/10 hover:border-white/20'
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Date + canteen filters */}
        <div className="flex flex-wrap gap-3">
          <div className="flex gap-2">
            <button onClick={() => { setDateFrom(todayStart()); setDateTo(null) }}
              className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${
                dateFrom === todayStart() && !dateTo ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' : 'bg-white/5 text-gray-500 border-white/10'
              }`}>Today</button>
            <button onClick={() => { const d = new Date(); d.setDate(d.getDate()-7); setDateFrom(d.toISOString()); setDateTo(null) }}
              className="text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border bg-white/5 text-gray-500 border-white/10 hover:border-white/20">
              This week
            </button>
          </div>

          <input type="date" onChange={e => setDateFrom(e.target.value ? new Date(e.target.value).toISOString() : null)}
            className="bg-[#121212] border border-white/10 rounded-lg px-3 py-1.5 text-gray-300 text-xs focus:border-orange-500 focus:outline-none" />

          <select onChange={e => setCanteen(e.target.value || null)}
            className="bg-[#121212] border border-white/10 rounded-lg px-3 py-1.5 text-gray-300 text-xs focus:border-orange-500 focus:outline-none">
            <option value="">All canteens</option>
            {canteens.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>

          <button onClick={exportCSV}
            className="ml-auto flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300 text-xs font-bold px-3 py-1.5 rounded-lg transition-all">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
        </div>
      </div>

      {/* Orders table */}
      {loading && orders.length === 0 ? <Spinner /> : (
        <TableWrap>
          <thead>
            <tr>
              <Th></Th>
              <Th>Order ID</Th>
              <Th>Canteen</Th>
              <Th>Student</Th>
              <Th>Status</Th>
              <Th right>Amount</Th>
              <Th>Slot</Th>
              <Th right>Token</Th>
              <Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => (
              <>
                <tr key={o.order_id}
                  onClick={() => expandRow(o.order_id)}
                  className="cursor-pointer hover:bg-white/[0.02] transition-colors">
                  <Td>
                    {expanded === o.order_id
                      ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                      : <ChevronRight className="w-3.5 h-3.5 text-gray-600" />}
                  </Td>
                  <Td mono><span className="text-gray-300">{o.order_id?.slice(0,12)}…</span></Td>
                  <Td><span className="text-gray-200">{o.canteen_name}</span></Td>
                  <Td mono><span className="text-gray-500">{o.student_masked}</span></Td>
                  <Td><Badge label={o.status} /></Td>
                  <Td right><span className="text-orange-400 font-medium">{formatRupees(o.total_amount)}</span></Td>
                  <Td><span className="text-gray-400">{o.pickup_slot}</span></Td>
                  <Td right><span className="text-gray-300">#{o.token_number}</span></Td>
                  <Td><span className="text-gray-500">{formatTime(o.created_at)}</span></Td>
                </tr>
                {expanded === o.order_id && (
                  <tr key={`${o.order_id}-detail`}>
                    <td colSpan={9} className="px-4 py-4 bg-[#121212] border-b border-white/5">
                      <OrderDetail data={detail[o.order_id]} orderId={o.order_id} />
                    </td>
                  </tr>
                )}
              </>
            ))}
            {orders.length === 0 && (
              <tr><td colSpan={9} className="text-center text-gray-600 py-8 text-xs">No orders match your filters</td></tr>
            )}
          </tbody>
        </TableWrap>
      )}

      {hasMore && (
        <button onClick={loadMore} disabled={loading}
          className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-gray-400 text-xs font-bold py-3 rounded-xl transition-all disabled:opacity-50">
          {loading ? 'Loading...' : `Load more (next ${PAGE_SIZE})`}
        </button>
      )}
    </div>
  )
}

function OrderDetail({ data, orderId }) {
  if (!data) return <p className="text-gray-600 text-xs">Loading...</p>

  const { order, items, payment, refund, ticket } = data

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 text-xs">
      {/* Order info */}
      <div className="space-y-2">
        <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">Order</p>
        <p className="text-gray-300 font-mono">{orderId}</p>
        <p className="text-gray-500">Slot: <span className="text-gray-300">{order?.pickup_slot}</span></p>
        <p className="text-gray-500">Token: <span className="text-gray-300">#{order?.token_number}</span></p>
        <p className="text-gray-500">Status: <Badge label={order?.status} /></p>
        {payment && <p className="text-gray-500">TXN: <span className="text-gray-300 font-mono">{payment.txn_id}</span></p>}
      </div>

      {/* Items */}
      <div className="space-y-2">
        <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px]">Items</p>
        {items?.map((i, idx) => (
          <div key={idx} className="flex justify-between text-gray-300">
            <span>{i.quantity}× {i.name}</span>
            <span className="text-orange-400">{formatRupees(i.price * i.quantity)}</span>
          </div>
        ))}
        <div className="border-t border-white/10 pt-1 flex justify-between font-bold text-gray-200">
          <span>Total</span>
          <span className="text-orange-400">{formatRupees(order?.total_amount)}</span>
        </div>
      </div>

      {/* Refund + ticket */}
      <div className="space-y-3">
        {refund && (
          <div>
            <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px] mb-1">Refund</p>
            <Badge label={refund.status} />
            <p className="text-gray-500 mt-1">Amount: <span className="text-gray-300">{formatRupees(refund.amount)}</span></p>
            <p className="text-gray-500">Reason: <span className="text-gray-300">{refund.reason}</span></p>
          </div>
        )}
        {ticket && (
          <div>
            <p className="text-gray-500 font-bold uppercase tracking-widest text-[10px] mb-1">Support ticket #{ticket.id}</p>
            <Badge label={ticket.status} />
            <p className="text-gray-400 mt-1 leading-relaxed">{ticket.description}</p>
          </div>
        )}
      </div>
    </div>
  )
}